import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Input, Select, Table, message, Spin } from "antd"
import { DownOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { ReferralsApi } from "../../Apis/referralsApi"
import { translations } from "../../utils/Translation/staticTranslation"

const PAGE_SIZE = 10

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (normalized === "claimed" || normalized === "success")
    return "status-success"
  if (normalized === "pending") return "status-pending"
  if (normalized === "expired") return "status-expired"
  if (normalized === "unclaimed") return "status-unclaimed"
  if (normalized === "failed") return "status-failed"
  return "status-default"
}

const formatStatusLabel = (status) => {
  const s = String(status || "").toLowerCase()
  if (!s) return "—"
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const isArabicLang = (lang) =>
  String(lang || "").toLowerCase().startsWith("ar")

const formatLogStatusLabel = (status, lang, t) => {
  const s = String(status || "").toLowerCase()
  if (isArabicLang(lang)) {
    if (s === "success") return t.filter_logs_success
    if (s === "failed") return t.filter_logs_failed
  }
  return formatStatusLabel(status)
}

/** Prefer API `activity_type`; fall back to label text for icon */
const activityTypeToIcon = (activityType) => {
  const t = String(activityType || "")
    .toLowerCase()
    .replace(/-/g, "_")
  const map = {
    signup: { icon: "/referrals/new-user-signup.svg", cls: "orange" },
    subscription: { icon: "/referrals/successful-conversion.svg", cls: "green" },
    pending_approval: { icon: "/referrals/pending-approval.svg", cls: "orange" },
    redeem: { icon: "/referrals/reward-claimed.svg", cls: "green" },
    redeem_points: { icon: "/referrals/reward-claimed.svg", cls: "green" },
    conversion: { icon: "/referrals/successful-conversion.svg", cls: "green" },
    successful_conversion: { icon: "/referrals/successful-conversion.svg", cls: "green" },
    link_click: { icon: "/referrals/referral-link-clicked.svg", cls: "blue" },
    referral_link_clicked: { icon: "/referrals/referral-link-clicked.svg", cls: "blue" },
    reward_claimed: { icon: "/referrals/reward-claimed.svg", cls: "green" },
    email_sent: { icon: "/referrals/referral-email-sent.svg", cls: "purple" },
    referral_email_sent: { icon: "/referrals/referral-email-sent.svg", cls: "purple" },
    social_share: { icon: "/referrals/social-share.svg", cls: "blue" },
    conversion_failed: { icon: "/referrals/conversion-failed.svg", cls: "red" },
  }
  return map[t] || null
}

const eventIconFromLabel = (event) => {
  const key = String(event || "").toLowerCase()
  if (key.includes("successful conversion"))
    return { icon: "/referrals/successful-conversion.svg", cls: "green" }
  if (key.includes("referral link clicked"))
    return { icon: "/referrals/referral-link-clicked.svg", cls: "blue" }
  if (key.includes("new user signup"))
    return { icon: "/referrals/new-user-signup.svg", cls: "orange" }
  if (key.includes("reward claimed"))
    return { icon: "/referrals/reward-claimed.svg", cls: "green" }
  if (key.includes("referral email sent"))
    return { icon: "/referrals/referral-email-sent.svg", cls: "purple" }
  if (key.includes("social share"))
    return { icon: "/referrals/social-share.svg", cls: "blue" }
  if (key.includes("conversion failed"))
    return { icon: "/referrals/conversion-failed.svg", cls: "red" }
  if (key.includes("reward approved"))
    return { icon: "/referrals/reward-claimed.svg", cls: "green" }
  return { icon: "/referrals/successful-conversion.svg", cls: "green" }
}

const resolveEventIcon = (activityType, activityLabel) => {
  return activityTypeToIcon(activityType) || eventIconFromLabel(activityLabel)
}

const formatLogTimestamp = (iso) => {
  if (!iso) return "—"
  return dayjs(iso).format("YYYY-MM-DD HH:mm:ss")
}

const mapApiRow = (row, lang, t) => {
  const useAr = isArabicLang(lang)
  const activityLabel = useAr
    ? String(row.activity_label_arabic || "").trim() ||
      String(row.activity_label || "").trim() ||
      "—"
    : String(row.activity_label || "").trim() || "—"
  const detailsRaw = useAr
    ? String(row.details_arabic || "").trim() ||
      String(row.details || "").trim()
    : String(row.details || "").trim()
  const details = detailsRaw || "—"

  return {
    key: row.id ?? `${row.created_at}-${row.user_email}`,
    activity_type: row.activity_type,
    activity_label: activityLabel,
    activity_label_for_icon:
      String(row.activity_label || "").trim() || activityLabel,
    user_name: row.user_name || "—",
    details,
    timestamp: formatLogTimestamp(row.created_at),
    status: row.status,
    statusLabel: formatLogStatusLabel(row.status, lang, t),
  }
}

const ActivityLogsTab = ({ readOnly = false }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.referralsPage || translations.en.referralsPage

  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [exportLoading, setExportLoading] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((prev) => {
        const next = search.trim()
        if (prev !== next) setPage(1)
        return next
      })
    }, 350)
    return () => clearTimeout(t)
  }, [search])

  const loadLogs = useCallback(async () => {
    setLoading(true)
    try {
      const res = await ReferralsApi.getActivityLogs({
        page,
        page_size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      const payload = res.data
      const result = payload?.result
      if (payload?.success && result && Array.isArray(result.results)) {
        setRows(result.results.map((row) => mapApiRow(row, lang, t)))
        setTotal(typeof result.count === "number" ? result.count : result.results.length)
      } else {
        setRows([])
        setTotal(0)
        message.error("Could not load activity logs.")
      }
    } catch (e) {
      setRows([])
      setTotal(0)
      message.error(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Failed to load activity logs."
      )
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch, statusFilter, lang, t])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  const handleExportCsv = useCallback(async () => {
    if (readOnly) return
    setExportLoading(true)
    try {
      const res = await ReferralsApi.exportActivityLogsCsv()
      const blob = res?.data
      if (!(blob instanceof Blob)) {
        message.error("Could not download export.")
        return
      }
      const cd = res.headers?.["content-disposition"]
      const match = cd?.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
      const fallback = `referral-activity-logs-${dayjs().format("YYYY-MM-DD")}.csv`
      const filename = match?.[1]?.trim() || fallback
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success("Export downloaded.")
    } catch (e) {
      const errBody = e?.response?.data
      if (errBody instanceof Blob) {
        try {
          const text = await errBody.text()
          const j = JSON.parse(text)
          message.error(
            j.message || j.error || j.detail || "Failed to export activity logs.",
          )
        } catch {
          message.error("Failed to export activity logs.")
        }
      } else {
        message.error(
          errBody?.message ||
            errBody?.error ||
            e?.message ||
            "Failed to export activity logs.",
        )
      }
    } finally {
      setExportLoading(false)
    }
  }, [readOnly])

  const logsColumns = useMemo(
    () => [
      {
        title: t.col_event,
        dataIndex: "activity_label",
        key: "activity",
        width: 280,
        render: (label, row) => {
          const info = resolveEventIcon(
            row.activity_type,
            row.activity_label_for_icon || label,
          )
          return (
            <div className="ref-logs-event-cell">
              <span className={`ref-logs-event-icon ${info.cls}`}>
                <img src={info.icon} alt="" className="ref-logs-event-icon-img" />
              </span>
              <span>{label}</span>
            </div>
          )
        },
      },
      { title: t.col_user, dataIndex: "user_name", key: "user_name" },
      { title: t.col_details, dataIndex: "details", key: "details" },
      { title: t.col_timestamp, dataIndex: "timestamp", key: "timestamp" },
      {
        title: t.status,
        dataIndex: "status",
        key: "status",
        render: (status, row) => (
          <span className={`ref-status-pill ${statusClass(status)}`}>
            {row.statusLabel}
          </span>
        ),
      },
    ],
    [t],
  )

  return (
    <div className="referrals-simple-view">
      <div className="referrals-subheader">
        <h2>{t.activity_logs_title}</h2>
        <button
          className="ref-button-primary ref-logs-export-btn"
          type="button"
          disabled={readOnly || exportLoading}
          title={readOnly ? t.read_only_hint : undefined}
          onClick={handleExportCsv}
        >
          {exportLoading ? t.exporting : t.export}
          <DownOutlined />
        </button>
      </div>
      <div className="ref-panel ref-logs-table-panel">
        <div className="ref-logs-filters-row">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.search_logs}
            className="ref-logs-search-input"
            allowClear
          />
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v)
              setPage(1)
            }}
            className="ref-logs-status-filter"
            options={[
              { label: t.filter_all_status, value: "all" },
              { label: t.filter_logs_success, value: "success" },
              { label: t.filter_logs_failed, value: "failed" },
            ]}
          />
        </div>
        <Spin spinning={loading}>
          <Table
            className="ref-ant-table ref-logs-table"
            columns={logsColumns}
            dataSource={rows}
            pagination={{
              current: page,
              total,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              position: ["bottomRight"],
              itemRender: (pageNumber, type, originalElement) => {
                if (type === "prev")
                  return (
                    <span className="ref-logs-pagination-link">{t.pagination_prev}</span>
                  )
                if (type === "next")
                  return (
                    <span className="ref-logs-pagination-link">{t.pagination_next}</span>
                  )
                if (type === "jump-prev" || type === "jump-next")
                  return <span className="ref-logs-pagination-ellipsis">...</span>
                if (type === "page") {
                  const isActive = pageNumber === page
                  return (
                    <span className={`ref-logs-pagination-page ${isActive ? "active" : ""}`}>
                      {pageNumber}
                    </span>
                  )
                }
                return originalElement
              },
              onChange: (nextPage) => setPage(nextPage),
            }}
            size="middle"
          />
        </Spin>
      </div>
    </div>
  )
}

export default ActivityLogsTab
